import { CalendarDays, Download, SlidersHorizontal } from 'lucide-react'
import { type ComponentProps, type ReactNode, useState } from 'react'
import { createPortal } from 'react-dom'
import { useViewportBand } from '../lib/viewport'
import { Button } from './Button'
import { OverflowGroup } from './OverflowGroup'
import { usePageToolSlot } from './PageAction'
import { Tooltip } from './Tooltip'
import './toolbar.css'

/**
 * The band of controls above a screen that works on an artifact rather than a
 * list — the luach, on screen and on its way to the printer.
 *
 * `TableHeader` is the same idea for a collection, and the two differ in what
 * they hold rather than in how they behave: a collection's controls are bare and
 * reached for by name (search, a filter, a view switch), while these are labeled
 * fields, because `5786` means nothing without "Year" above it. Both collapse
 * into the same row through the same hook.
 *
 * ## Three slots
 *
 * - `fields` — what the artifact is OF. Year, occasion, date.
 * - `actions` — what you do with it. Step a week, download it.
 * - `options` — what it looks like when it prints. Revealed, not permanent.
 *
 * **The band owns the control that reveals `options`.** A screen passes the
 * options and never the toggle: a screen that drew its own would have to know
 * that the toggle is meaningless on a phone, where the options are simply in the
 * panel already — and a control that opens a panel from inside the panel it
 * opened is the shape this whole rule exists to prevent.
 *
 * ## Why `options` is a revealed ROW on a desk and not a popover
 *
 * You toggle "English date" to watch the sheet change. A panel floating over the
 * sheet hides the thing the toggle is for. So on a wide screen it pushes the
 * content down instead — it is not sticky, so nothing offsets from it and
 * `--topbar-h` is untouched.
 *
 * ## And why that stops on a phone
 *
 * **One entry point per slot, as cells of one row.** Three fields, four actions
 * and four options cannot be laid out at 375px — they are four stories and three
 * hundred pixels of chrome before a word of the luach. Each slot collapses
 * whole-group instead, and the slots that a screen actually filled become equal
 * cells divided by hairlines: one takes the row, two take halves, three take
 * thirds. That is the shell's own row, and it is derived from what was filled
 * rather than from a number, so a band with no options has two cells and nothing
 * needs adjusting.
 *
 * A cell each rather than one entry point for all three: a third of the row is a
 * target you cannot miss and each one says which it is, where a single glyph on
 * an otherwise empty band names nothing.
 *
 * Inside the app the row lives in the shell. On a **public** page there is no
 * shell and therefore no row, so the band becomes that row in place — the rule
 * is about the width of a phone, not about which surface the page is on.
 */
export function Toolbar({
  fields,
  actions,
  options,
  optionsLabel = 'Sheet options',
  fieldsLabel = 'Choose the sheet',
  fieldsShort = 'Sheet',
  fieldsIcon = CalendarDays,
  actionsLabel = 'Sheet actions',
  actionsShort = 'Download',
  actionsIcon = Download,
  fit,
}: {
  fields?: ReactNode
  actions?: ReactNode
  options?: ReactNode
  optionsLabel?: string
  /**
   * What the entry points the fields and the actions become are called, and
   * the glyph each wears. A band names them for the artifact it works on:
   * the luach's are a sheet and its downloads.
   */
  fieldsLabel?: string
  fieldsShort?: string
  fieldsIcon?: ComponentProps<typeof OverflowGroup>['icon']
  actionsLabel?: string
  actionsShort?: string
  actionsIcon?: ComponentProps<typeof OverflowGroup>['icon']
  /**
   * The fields size to their CONTENT instead of sharing the row.
   *
   * A band of selects wants the width — an occasion's name is long and the
   * chooser should be as wide as the band allows. A band of fixed-size controls
   * does not: two steppers given half a page each sit stranded at the far ends
   * of empty cells, which reads as chrome that came loose rather than as a
   * band. Which of the two a band is depends on what a screen put in it, so the
   * screen says.
   */
  fit?: boolean
}) {
  const band = useViewportBand()
  const slot = usePageToolSlot()
  const [optionsOpen, setOptionsOpen] = useState(false)
  if (band === 'handheld') {
    const entry = (
      <div className="toolbar toolbar--row">
        {fields ? (
          <OverflowGroup
            label={fieldsLabel}
            short={fieldsShort}
            icon={fieldsIcon}
            align="start"
            surface="panel"
            forceCollapsed
          >
            <div className="toolbar__fields">{fields}</div>
          </OverflowGroup>
        ) : null}
        {actions ? (
          <OverflowGroup
            label={actionsLabel}
            short={actionsShort}
            icon={actionsIcon}
            align="start"
            surface="panel"
            forceCollapsed
          >
            <div className="toolbar__actions">{actions}</div>
          </OverflowGroup>
        ) : null}
        {options ? (
          <OverflowGroup
            label={optionsLabel}
            short="Options"
            icon={SlidersHorizontal}
            align="end"
            surface="panel"
            forceCollapsed
          >
            <div className="toolbar__options">{options}</div>
          </OverflowGroup>
        ) : null}
      </div>
    )
    // Into the shell's row where there is one; in place where there is not.
    return slot ? createPortal(entry, slot) : entry
  }

  const row =
    actions || options ? (
      <div className="toolbar__actions">
        {actions}
        {options ? (
          <Tooltip label={optionsLabel}>
            <Button
              variant="secondary"
              icon
              on={optionsOpen}
              aria-label={optionsLabel}
              aria-expanded={optionsOpen}
              onClick={() => setOptionsOpen((v) => !v)}
            >
              <SlidersHorizontal className="btn__icon" aria-hidden />
            </Button>
          </Tooltip>
        ) : null}
      </div>
    ) : null

  return (
    <>
      <div className="toolbar" {...(fit ? { 'data-fit': '' } : {})}>
        {fields ? <div className="toolbar__fields">{fields}</div> : null}
        {/* Fields that share the row give way first, so the actions beside
            them always fit. Fields sized to their content do not give way,
            so there the actions become one entry point where they run short. */}
        {row && fit ? (
          <OverflowGroup label={actionsLabel}>{row}</OverflowGroup>
        ) : (
          row
        )}
      </div>
      {optionsOpen && options ? (
        <div className="optionsbar">{options}</div>
      ) : null}
    </>
  )
}
